import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { IMaskInput } from "react-imask";
import {
  Check,
  Copy,
  Crown,
  Sparkles,
  Users,
  User,
  Zap,
  FileText,
  AlertCircle,
  Shield,
} from "lucide-react";
import {
  PERIODOS,
  PLANOS_PAGOS,
  PLANO_TRIAL,
  FORMAS_PAGAMENTO,
  STATUS_LABELS,
  calcularPrecoPeriodo,
  calcularEquivalenteMensal,
  formatarMoeda,
  labelPlano,
  labelPeriodo,
} from "../constants/planos";
import {
  cancelarAssinaturaApi,
  cancelarPagamentoPendenteApi,
  cancelarTrocaAgendadaApi,
  agendarTrocaPlanoApi,
  contratarAssinatura,
  fetchAssinaturaStatus,
  renovarAssinaturaApi,
  desativarRenovacaoAutomaticaApi,
  simularPagamentoApi,
  sincronizarPagamentoPendenteApi,
} from "../api/assinaturaApi";
import { sessionManager } from "../utils/sessionManager";
import { detectarBandeiraCartao } from "../utils/cartaoBandeira";
import BandeiraCartao from "../components/BandeiraCartao";
import FormaPagamentoIcon from "../components/FormaPagamentoIcon";
import LoadingOverlay from "../components/LoadingOverlay";
import IconePix from "../components/IconePix";
import IconeCartaoCredito from "../components/IconeCartaoCredito";
import VencimentoCountdown from "../components/VencimentoCountdown";
import dayjs from "dayjs";
import { useIsMobile } from "../hooks/useMediaQuery";

const labelStatusFatura = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "pago") return "Pago";
  if (key === "cancelado") return "Cancelado";
  if (key === "pendente") return "Pendente";
  return status || "—";
};

const classeStatusFatura = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "pago") return "badge-ok";
  if (key === "cancelado") return "badge-cancel";
  return "badge-pending";
};

const iconePlano = {
  solo: User,
  equipe: Users,
  pro: Crown,
  trial: Sparkles,
};

const TERMOS_GENERICOS = `Ao contratar, você declara estar ciente de que a assinatura é por estabelecimento, com cobrança antecipada do período escolhido. Renovação automática aplica-se somente ao cartão de crédito. PIX e boleto exigem pagamento manual a cada período. O cancelamento suspende o acesso na hora.`;

const CARTAO_TESTE = {
  numero: "5162 3062 1937 8829",
  nome: "MARCELO H ALMEIDA",
  validade: "12/30",
  cvv: "318",
};

const CARTAO_TESTE_MOCK = {
  numero: "4111 1111 1111 1111",
  nome: "TESTE APROVADO",
  validade: "12/30",
  cvv: "123",
};

const formatarNumeroCartao = (valor) => {
  const digits = String(valor || "").replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

const formatarValidadeCartao = (valor) => {
  const digits = String(valor || "").replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const validarCartao = ({ numero, nome, validade, cvv }) => {
  const num = String(numero || "").replace(/\D/g, "");
  const val = String(validade || "").trim();
  const cod = String(cvv || "").replace(/\D/g, "");
  if (num.length < 13 || num.length > 19) return "Informe um número de cartão válido (13 a 19 dígitos).";
  if (!String(nome || "").trim()) return "Informe o nome no cartão.";
  if (!/^\d{2}\/\d{2}$/.test(val)) return "Validade no formato MM/AA.";
  const [mm] = val.split("/").map(Number);
  if (mm < 1 || mm > 12) return "Mês de validade inválido.";
  if (cod.length < 3 || cod.length > 4) return "Informe o CVV (3 ou 4 dígitos).";
  return null;
};

const MENSAGEM_PAGAMENTO_GENERICA =
  "Não foi possível concluir o pagamento. Verifique os dados e tente novamente, ou use PIX.";

const mensagemAmigavelPagamento = (mensagemRaw, { status } = {}) => {
  const mensagem = String(mensagemRaw || "").trim();
  const lower = mensagem.toLowerCase();

  if (
    !mensagem ||
    /network error|timeout|econnaborted|failed to fetch|networkerror|err_network/i.test(mensagem)
  ) {
    return "Não foi possível conectar ao pagamento agora. Tente novamente em alguns instantes.";
  }

  if (
    /não autorizad|nao autorizad|recusad|declin|unauthorized|tokeniza|aguarde para tentar|risk analysis|an[aá]lise de risco|blocked|bloquead|suspeit/i.test(
      lower
    )
  ) {
    return "O pagamento no cartão não foi autorizado. Confira os dados do titular e do cartão, aguarde alguns minutos e tente de novo, ou pague via PIX.";
  }

  if (
    /j[aá] tokenizado|creditcardtoken|invalid_creditcard|cart[aã]o inv[aá]lid|n[uú]mero do cart[aã]o|cvv|expiry|validade do cart/i.test(
      lower
    )
  ) {
    return "Não foi possível processar este cartão. Confira os dados ou tente outro cartão, ou pague via PIX.";
  }

  if (
    status === 402 ||
    /n[aã]o foi poss[ií]vel confirmar|confirmar o pagamento|pagamento.*n[aã]o.*confirm/i.test(lower)
  ) {
    return "O pagamento no cartão não foi confirmado. Tente novamente ou pague via PIX.";
  }

  if (/cliente removido|customer removed|customer unavailable/i.test(lower)) {
    return "Houve um problema ao configurar o pagamento. Tente novamente em instantes.";
  }

  if (
    /(cpf|cnpj|documento|titular|cep|endere[cç]o|telefone|e-?mail)/i.test(mensagem) &&
    /(informe|inv[aá]lid|obrigat|preencha)/i.test(mensagem)
  ) {
    return mensagem;
  }

  // Mensagens técnicas do Asaas / inglês / códigos internos
  if (
    /asaas|billingtype|creditcard|holderinfo|invalid_|error code|status code|axios|request failed/i.test(
      mensagem
    ) ||
    mensagem.length > 160
  ) {
    return MENSAGEM_PAGAMENTO_GENERICA;
  }

  // Mensagem curta em português vinda do nosso backend — mantém
  if (mensagem.length <= 140) return mensagem;

  return MENSAGEM_PAGAMENTO_GENERICA;
};

const extrairErroAssinatura = (error) => {
  const status = error?.response?.status;
  const raw =
    error?.response?.data?.mensagem ||
    error?.response?.data?.message ||
    (error?.code === "ECONNABORTED" || error?.code === "ERR_NETWORK"
      ? "timeout"
      : null) ||
    error?.message;
  return mensagemAmigavelPagamento(raw, { status });
};

const mascaraCpfCnpj = [
  { mask: "000.000.000-00" },
  { mask: "00.000.000/0000-00" },
];

const validarTitularCartao = (titular) => {
  const doc = String(titular?.cpfCnpj || "").replace(/\D/g, "");
  if (doc.length !== 11 && doc.length !== 14) return "Informe o CPF ou CNPJ do titular do cartão.";
  const email = String(titular?.email || "").trim();
  if (!email || !email.includes("@")) return "Informe o e-mail do titular do cartão.";
  const tel = String(titular?.telefone || "").replace(/\D/g, "");
  if (tel.length < 10) return "Informe o telefone do titular do cartão.";
  const cep = String(titular?.cep || "").replace(/\D/g, "");
  if (cep.length !== 8) return "Informe o CEP do titular do cartão.";
  if (!String(titular?.enderecoNumero || "").trim()) return "Informe o número do endereço do titular.";
  return null;
};

const aguardar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const textoCountdownRenovacao = (dias) => {
  if (dias === 0) return "Hoje é o último dia para renovar.";
  if (dias === 1) return "Falta 1 dia para o vencimento.";
  return `Faltam ${dias} dias para o vencimento.`;
};

const PIX_VALIDADE_MINUTOS = 30;

const PixPagamentoPanel = ({
  pagamento,
  valor,
  geradoEm,
  onSimular,
  onVerificarPagamento,
  onCancelarPix,
  modoTeste,
  asaasSandbox,
  integracaoAsaas,
  podeCancelar,
  processando,
  processandoVerificar,
  processandoCancelar,
  onRecarregarQr,
}) => {
  const [copiado, setCopiado] = useState(false);
  const [tempoRestante, setTempoRestante] = useState("");

  const expiraExibicao = useMemo(() => {
    const expiraAsaas = pagamento?.pixExpiraEm || pagamento?.expiraEm;
    const expiraApi = expiraAsaas ? dayjs(expiraAsaas) : null;

    if (integracaoAsaas && !asaasSandbox && expiraApi?.isValid()) {
      return expiraApi;
    }

    const gerado = geradoEm ? dayjs(geradoEm) : null;
    const limitePainel = gerado?.isValid() ? gerado.add(PIX_VALIDADE_MINUTOS, "minute") : null;

    if (asaasSandbox && limitePainel) return limitePainel;
    if (limitePainel && expiraApi?.isValid()) {
      return expiraApi.isBefore(limitePainel) ? expiraApi : limitePainel;
    }
    return expiraApi?.isValid() ? expiraApi : limitePainel;
  }, [geradoEm, asaasSandbox, integracaoAsaas, pagamento?.pixExpiraEm, pagamento?.expiraEm]);

  useEffect(() => {
    if (!expiraExibicao?.isValid()) return undefined;
    const atualizar = () => {
      const diff = expiraExibicao.diff(dayjs());
      if (diff <= 0) {
        setTempoRestante("Expirado");
        return;
      }
      const min = Math.floor(diff / 60000);
      const seg = Math.floor((diff % 60000) / 1000);
      setTempoRestante(`${String(min).padStart(2, "0")}:${String(seg).padStart(2, "0")}`);
    };
    atualizar();
    const timer = setInterval(atualizar, 1000);
    return () => clearInterval(timer);
  }, [expiraExibicao]);

  useEffect(() => {
    if (tempoRestante !== "Expirado" || !onRecarregarQr) return undefined;
    const timer = setTimeout(() => {
      onRecarregarQr();
    }, 1500);
    return () => clearTimeout(timer);
  }, [tempoRestante, onRecarregarQr]);

  const copiarPix = async () => {
    try {
      await navigator.clipboard.writeText(pagamento.pixQrCode);
      setCopiado(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.error("Não foi possível copiar. Selecione o código manualmente.");
    }
  };

  if (!pagamento?.pixQrCode) {
    return (
      <div className="assinatura-pix-panel assinatura-pix-panel--loading">
        <div className="assinatura-pix-panel__header">
          <div className="assinatura-pix-panel__badge">
            <IconePix size={18} />
            PIX
          </div>
          {valor != null && (
            <p className="assinatura-pix-panel__valor">{formatarMoeda(valor)}</p>
          )}
        </div>
        <p className="muted">Gerando QR Code PIX… aguarde alguns segundos.</p>
        {onRecarregarQr && (
          <button type="button" className="btn btn-outline" onClick={onRecarregarQr}>
            Atualizar QR Code
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="assinatura-pix-panel">
      <div className="assinatura-pix-panel__header">
        <div className="assinatura-pix-panel__badge">
          <IconePix size={18} />
          PIX
        </div>
        {valor != null && (
          <p className="assinatura-pix-panel__valor">{formatarMoeda(valor)}</p>
        )}
        {expiraExibicao?.isValid() && (
          <p className={`assinatura-pix-panel__timer ${tempoRestante === "Expirado" ? "is-expired" : ""}`}>
            {tempoRestante === "Expirado"
              ? "Código expirado — gere uma nova cobrança"
              : `Válido por ${tempoRestante}`}
          </p>
        )}
      </div>

      {asaasSandbox && (
        <div className="assinatura-alert assinatura-alert--warning" style={{ marginBottom: "1rem" }}>
          <AlertCircle size={18} />
          <span>
            <strong>Sandbox Asaas:</strong> este PIX não funciona no app do banco real (certificado de teste).
            Confirme em <strong>sandbox.asaas.com → Cobranças → Confirmar pagamento</strong>, ou use{" "}
            <strong>Já paguei — verificar pagamento</strong> abaixo.
          </span>
        </div>
      )}

      <div className="assinatura-pix-panel__body">
        <div className="assinatura-pix-panel__qr-wrap">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=12&data=${encodeURIComponent(pagamento.pixQrCode)}`}
            alt="QR Code para pagamento PIX"
            className="assinatura-pix-qr"
          />
          <span className="assinatura-pix-panel__qr-hint">
            {asaasSandbox ? "QR para teste (sandbox)" : "Escaneie no app do banco"}
          </span>
        </div>

        <div className="assinatura-pix-panel__steps">
          <ol>
            {asaasSandbox ? (
              <>
                <li>No Asaas sandbox, abra <strong>Cobranças</strong></li>
                <li>Localize esta cobrança e clique em <strong>Confirmar pagamento</strong></li>
                <li>Volta aqui e clique em <strong>Já paguei — verificar pagamento</strong></li>
                <li>O plano ativa automaticamente após confirmação</li>
              </>
            ) : (
              <>
                <li>Abra o app do seu banco</li>
                <li>Escolha pagar com PIX (QR ou copia e cola)</li>
                <li>Confirme o valor e finalize</li>
                <li>O plano libera automaticamente após confirmação</li>
              </>
            )}
          </ol>

          <label className="assinatura-pix-panel__copia-label">Copia e cola</label>
          <div className="assinatura-pix-panel__copia-row">
            <code className="assinatura-pix-panel__copia-code">{pagamento.pixQrCode}</code>
            <button type="button" className="btn btn-outline btn-sm" onClick={copiarPix}>
              {copiado ? <Check size={16} /> : <Copy size={16} />}
              {copiado ? "Copiado" : "Copiar"}
            </button>
          </div>
        </div>
      </div>

      {integracaoAsaas && !modoTeste && (
        <p className="muted assinatura-pix-panel__auto-hint">
          Após pagar no app do banco, o plano ativa automaticamente em alguns segundos.
        </p>
      )}

      {modoTeste && (
        <div className="assinatura-pix-panel__dev">
          <p className="muted">Ambiente de teste</p>
          <button type="button" className="btn btn-outline" onClick={onSimular} disabled={processando}>
            Simular pagamento confirmado
          </button>
        </div>
      )}

      {modoTeste && (
        <div className="assinatura-pix-panel__dev">
          <p className="muted">
            {asaasSandbox
              ? "Após confirmar no sandbox, use o botão abaixo se o plano não ativar sozinho."
              : "Use o botão abaixo se o plano não ativar automaticamente."}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onVerificarPagamento}
            disabled={processandoVerificar}
          >
            {processandoVerificar ? "Verificando…" : "Já paguei — verificar pagamento"}
          </button>
        </div>
      )}

      {podeCancelar && (
        <div className="assinatura-pix-panel__cancel">
          <p className="muted">
            Para pagar com <strong>cartão</strong>, cancele esta cobrança PIX e escolha novamente no checkout.
          </p>
          <button
            type="button"
            className="btn btn-outline btn-danger-outline"
            onClick={onCancelarPix}
            disabled={processando || processandoCancelar}
          >
            {processandoCancelar ? "Cancelando…" : "Cancelar cobrança PIX"}
          </button>
        </div>
      )}
    </div>
  );
};

const TermosAceite = ({ aceite, onChange, disabled, onVerTermos, destacado = false }) => {
  const alternar = () => {
    if (!disabled) onChange(!aceite);
  };

  return (
    <div
      className={`assinatura-termos${aceite ? " is-accepted" : ""}${disabled ? " is-disabled" : ""}${destacado ? " is-highlight" : ""}`}
      role="checkbox"
      aria-checked={aceite}
      tabIndex={disabled ? -1 : 0}
      onClick={(event) => {
        if (event.target.closest(".assinatura-termos__link")) return;
        alternar();
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          alternar();
        }
      }}
    >
      <span className="assinatura-termos__check" aria-hidden="true">
        {aceite ? <Check size={16} strokeWidth={3} /> : null}
      </span>
      <span className="assinatura-termos__text">
        Li e aceito os{" "}
        <button
          type="button"
          className="assinatura-termos__link"
          onClick={(event) => {
            event.stopPropagation();
            onVerTermos(event);
          }}
        >
          Termos de uso
        </button>
        {" "}e a{" "}
        <button
          type="button"
          className="assinatura-termos__link"
          onClick={(event) => {
            event.stopPropagation();
            onVerTermos(event);
          }}
        >
          Política de assinatura
        </button>
        .
      </span>
    </div>
  );
};

const Assinatura = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const usuario = sessionManager.obterUsuario();
  const tipo = String(usuario?.tipo || "").toLowerCase();
  const isSuperAdmin = tipo === "super_admin";
  const isAdmin = tipo === "admin" || isSuperAdmin;
  const podeGerenciar = tipo === "admin";

  const [assinatura, setAssinatura] = useState(null);
  const [acesso, setAcesso] = useState(null);
  const [isento, setIsento] = useState(false);
  const [permiteSimular, setPermiteSimular] = useState(false);
  const [integracaoAsaas, setIntegracaoAsaas] = useState(false);
  const [asaasSandbox, setAsaasSandbox] = useState(false);
  const [documentoFaturamentoOk, setDocumentoFaturamentoOk] = useState(true);
  const [processandoVerificarPix, setProcessandoVerificarPix] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [overlayPagamento, setOverlayPagamento] = useState(null);
  const [processandoCancelarPix, setProcessandoCancelarPix] = useState(false);
  const [processandoTroca, setProcessandoTroca] = useState(false);
  const [checkoutRenovacaoAberto, setCheckoutRenovacaoAberto] = useState(false);
  const modoRenovacaoAnteriorRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const pixPanelRef = useRef(null);
  const deveScrollPixRef = useRef(false);
  const termosRef = useRef(null);
  const [termosDestacado, setTermosDestacado] = useState(false);

  const recarregar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setLoading(true);
    try {
      const data = await fetchAssinaturaStatus();
      setIsento(Boolean(data?.isento));
      setAssinatura(data?.assinatura || null);
      setAcesso(data?.acesso || null);
      setPermiteSimular(Boolean(data?.permiteSimularPagamento));
      setIntegracaoAsaas(Boolean(data?.integracaoAsaas));
      setAsaasSandbox(Boolean(data?.asaasSandbox));
      setDocumentoFaturamentoOk(data?.documentoFaturamentoOk !== false);
      if (data?.pagamentoPendente) {
        setPagamentoPendente(data.pagamentoPendente);
      } else {
        setPagamentoPendente(null);
      }
    } catch (error) {
      console.error("Erro ao carregar assinatura:", error);
      if (!silencioso) {
        toast.error("Não foi possível carregar os dados da assinatura.");
      }
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, []);

  useEffect(() => {
    recarregar();
    const handler = () => { void recarregar(); };
    window.addEventListener("fz:assinatura:changed", handler);
    window.addEventListener("fz:estabelecimento:changed", handler);
    return () => {
      window.removeEventListener("fz:assinatura:changed", handler);
      window.removeEventListener("fz:estabelecimento:changed", handler);
    };
  }, [recarregar]);

  const notificarMudanca = () => {
    try {
      window.dispatchEvent(new CustomEvent("fz:assinatura:changed"));
    } catch {
      /* ignore */
    }
  };

  const [periodo, setPeriodo] = useState("monthly");
  const [planoPosTrial, setPlanoPosTrial] = useState("solo");
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [formaPagamento, setFormaPagamento] = useState("pix");
  const [aceiteTermos, setAceiteTermos] = useState(false);
  const [pagamentoPendente, setPagamentoPendente] = useState(null);
  const [cartao, setCartao] = useState({ numero: "", nome: "", validade: "", cvv: "" });
  const [titular, setTitular] = useState({
    cpfCnpj: "",
    email: "",
    telefone: "",
    cep: "",
    enderecoNumero: "",
  });

  const statusAtual = assinatura?.status || "pending_plan";
  const emCarencia = Boolean(assinatura?.carenciaAte || acesso?.motivo === "carencia");
  const planoAtual = assinatura?.plano || null;
  const planoAtivoPago = statusAtual === "active" && !emCarencia;
  const upgradeAgendado = assinatura?.upgradeAgendado || null;
  const proximaCobranca = assinatura?.proximaCobranca || null;
  const formaPagamentoAtual = assinatura?.formaPagamento || assinatura?.forma_pagamento || null;
  const labelFormaPagamento = FORMAS_PAGAMENTO.find((f) => f.id === formaPagamentoAtual)?.label || formaPagamentoAtual;
  const renovacaoManual = Boolean(assinatura?.renovacaoManual);
  const janelaRenovacaoAberta = Boolean(assinatura?.janelaRenovacaoAberta);
  const podeAgendarTroca = Boolean(assinatura?.podeAgendarTroca);
  const podeRenovar = Boolean(assinatura?.podeRenovar) || emCarencia;
  const podeDesativarRenovacaoAutomatica = Boolean(assinatura?.podeDesativarRenovacaoAutomatica);
  const renovacaoViaPix =
    formaPagamentoAtual === "pix" ||
    (formaPagamentoAtual === "cartao" && !assinatura?.renovacaoAutomatica);
  const renovacaoViaCartaoAuto =
    formaPagamentoAtual === "cartao" && Boolean(assinatura?.renovacaoAutomatica);
  const modoRenovacao =
    (emCarencia || (janelaRenovacaoAberta && renovacaoManual)) && (planoAtivoPago || emCarencia);
  const podeContratar = (!planoAtivoPago || modoRenovacao) && !isSuperAdmin;
  const diasRestantes = assinatura?.diasRestantes ?? 0;
  const ultimoDiaRenovacao =
    diasRestantes === 0 && Boolean(assinatura?.periodoFim) && !assinatura?.renovacaoAutomatica;
  const faturaPendente = useMemo(
    () => (assinatura?.faturas || []).find((f) => f.status === "pendente"),
    [assinatura?.faturas]
  );

  const temPixPendente = useMemo(() => {
    const forma = pagamentoPendente?.formaPagamento || faturaPendente?.forma;
    const temQr = Boolean(
      pagamentoPendente?.pixQrCode ||
      faturaPendente?.pixQrCode
    );
    return (
      (statusAtual === "awaiting_payment" ||
        statusAtual === "active" ||
        Boolean(pagamentoPendente)) &&
      (forma === "pix" || temQr)
    );
  }, [statusAtual, pagamentoPendente, faturaPendente]);

  const mostrarCheckoutRenovacao = modoRenovacao && checkoutRenovacaoAberto && !temPixPendente;

  const pagamentoPixAtivo = useMemo(() => {
    if (pagamentoPendente?.formaPagamento === "pix" || pagamentoPendente?.pixQrCode) {
      return pagamentoPendente;
    }
    if (faturaPendente?.forma === "pix" || faturaPendente?.pixQrCode) {
      return {
        formaPagamento: "pix",
        pixQrCode: faturaPendente.pixQrCode || null,
        pixExpiraEm: faturaPendente.expiraEm,
      };
    }
    return null;
  }, [pagamentoPendente, faturaPendente]);

  useEffect(() => {
    if (!temPixPendente || pagamentoPixAtivo?.pixQrCode) return undefined;
    let tentativas = 0;
    const timer = setInterval(() => {
      tentativas += 1;
      void recarregar({ silencioso: true });
      if (tentativas >= 5) clearInterval(timer);
    }, 2000);
    return () => clearInterval(timer);
  }, [temPixPendente, pagamentoPixAtivo?.pixQrCode, recarregar]);

  useEffect(() => {
    if (!deveScrollPixRef.current || !pixPanelRef.current) return;
    deveScrollPixRef.current = false;
    const timer = setTimeout(() => {
      pixPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(timer);
  }, [pagamentoPendente, temPixPendente, pagamentoPixAtivo?.pixQrCode]);

  const mostrarTermos = (event) => {
    event.preventDefault();
    event.stopPropagation();
    Swal.fire({
      title: "Termos de uso e Política de assinatura",
      html: `<div style="text-align:left;font-size:14px;line-height:1.5;">${TERMOS_GENERICOS}</div>`,
      confirmButtonText: "Fechar",
    });
  };

  const abrirCheckoutRenovacao = (planoId) => {
    if (planoId) setPlanoSelecionado(planoId);
    setCheckoutRenovacaoAberto(true);
  };

  const alertarTermosPendentes = () => {
    termosRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTermosDestacado(true);
    window.setTimeout(() => setTermosDestacado(false), 1800);

    Swal.fire({
      title: "Aceite os termos para continuar",
      html: "Marque a caixa <strong>Li e aceito os Termos de uso e a Política de assinatura</strong> antes de pagar.",
      icon: "info",
      confirmButtonText: "Entendi",
      confirmButtonColor: "#ff6b00",
    });
  };

  useEffect(() => {
    if (modoRenovacao) {
      setFormaPagamento(formaPagamentoAtual === "cartao" ? "cartao" : "pix");
    } else {
      setCheckoutRenovacaoAberto(false);
    }
  }, [modoRenovacao, formaPagamentoAtual]);

  useEffect(() => {
    if (modoRenovacao && !modoRenovacaoAnteriorRef.current && planoAtual) {
      setPlanoSelecionado(planoAtual);
    }
    modoRenovacaoAnteriorRef.current = modoRenovacao;
  }, [modoRenovacao, planoAtual]);

  useEffect(() => {
    if (location.state?.abrirRenovacao && modoRenovacao) {
      setCheckoutRenovacaoAberto(true);
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state, location.pathname, modoRenovacao]);

  useEffect(() => {
    if (!mostrarCheckoutRenovacao) return undefined;
    const timer = setTimeout(() => {
      document.querySelector(".assinatura-checkout")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [mostrarCheckoutRenovacao]);

  useEffect(() => {
    if (assinatura?.upgradeAgendado?.periodo) {
      setPeriodo(assinatura.upgradeAgendado.periodo);
    } else if (planoAtivoPago && assinatura?.periodo) {
      setPeriodo(assinatura.periodo);
    }
  }, [planoAtivoPago, assinatura?.periodo, assinatura?.upgradeAgendado]);

  useEffect(() => {
    if (planoAtivoPago && planoAtual && !modoRenovacao) {
      setPlanoSelecionado(planoAtual);
    } else if (!planoAtivoPago && !planoSelecionado) {
      setPlanoSelecionado("solo");
    }
  }, [planoAtivoPago, planoAtual, modoRenovacao, planoSelecionado]);

  const planoEscolhido = useMemo(
    () => PLANOS_PAGOS.find((p) => p.id === planoSelecionado) || PLANOS_PAGOS[0],
    [planoSelecionado]
  );
  const precoCheckout = useMemo(
    () => calcularPrecoPeriodo(planoEscolhido.precoMensal, periodo),
    [planoEscolhido, periodo]
  );
  const bandeiraCartao = useMemo(
    () => detectarBandeiraCartao(cartao.numero),
    [cartao.numero]
  );

  const modoTesteAssinatura = asaasSandbox || permiteSimular;

  const mostrarCancelar =
    modoTesteAssinatura &&
    podeGerenciar &&
    (statusAtual === "active" || statusAtual === "trial") &&
    !emCarencia;

  const bannerTone = useMemo(() => {
    if (isSuperAdmin) return "info";
    if (statusAtual === "suspended" || statusAtual === "pending_plan" || statusAtual === "awaiting_payment" || emCarencia) return "warning";
    if (statusAtual === "trial") return "trial";
    return "success";
  }, [statusAtual, isSuperAdmin, emCarencia]);

  const handleContratar = async (planoId, isTrial = false) => {
    if (!podeGerenciar) {
      toast.warn("Somente o administrador do estabelecimento pode contratar planos.");
      return;
    }
    if (isTrial && assinatura?.trialUsado) {
      toast.error("Este estabelecimento já utilizou o período de trial.");
      return;
    }
    if (!aceiteTermos) {
      alertarTermosPendentes();
      return;
    }
    if (!isTrial && !planoId) {
      toast.warn("Selecione um plano.");
      return;
    }
    if (!isTrial && formaPagamento === "cartao") {
      const erroTitular = validarTitularCartao(titular);
      if (erroTitular) {
        toast.warn(erroTitular);
        return;
      }
      const erroCartao = validarCartao(cartao);
      if (erroCartao) {
        toast.warn(erroCartao);
        return;
      }
    }
    if (!isTrial && (formaPagamento === "pix" || formaPagamento === "boleto") && !documentoFaturamentoOk) {
      Swal.fire({
        icon: "warning",
        title: "CPF/CNPJ de cobrança pendente",
        text: "Cadastre o CPF ou CNPJ de cobrança em Estabelecimentos antes de gerar o PIX.",
        confirmButtonText: "Ir para Estabelecimentos",
        showCancelButton: true,
        cancelButtonText: "Agora não",
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/estabelecimentos";
        }
      });
      return;
    }

    const planoFinal = isTrial ? planoPosTrial : planoId;

    setProcessando(true);
    setOverlayPagamento(
      isTrial
        ? "Ativando trial…"
        : formaPagamento === "pix"
          ? "Gerando PIX…"
          : "Processando pagamento…"
    );

    const cartaoApi =
      !isTrial && formaPagamento === "cartao" && integracaoAsaas
        ? {
            numero: cartao.numero,
            nome: cartao.nome,
            validade: cartao.validade,
            cvv: cartao.cvv,
          }
        : undefined;

    const titularApi =
      !isTrial && formaPagamento === "cartao"
        ? {
            nome: String(cartao.nome || "").trim(),
            cpfCnpj: String(titular.cpfCnpj || "").replace(/\D/g, ""),
            email: String(titular.email || "").trim(),
            telefone: String(titular.telefone || "").replace(/\D/g, ""),
            cep: String(titular.cep || "").replace(/\D/g, ""),
            enderecoNumero: String(titular.enderecoNumero || "").trim(),
          }
        : undefined;

    try {
      if (!isTrial && formaPagamento === "cartao" && !integracaoAsaas) {
        await aguardar(1800);
      }

      const data = modoRenovacao
        ? await renovarAssinaturaApi({
            plano: planoFinal,
            periodo,
            formaPagamento,
            aceiteTermos: true,
            cartao: cartaoApi,
            titular: titularApi,
            cartaoUltimos4:
              formaPagamento === "cartao"
                ? String(cartao.numero).replace(/\D/g, "").slice(-4)
                : undefined,
          })
        : await contratarAssinatura({
            plano: planoFinal,
            periodo,
            formaPagamento: isTrial ? undefined : formaPagamento,
            iniciarTrial: isTrial,
            aceiteTermos: true,
            cartao: cartaoApi,
            titular: titularApi,
            cartaoUltimos4:
              !isTrial && formaPagamento === "cartao"
                ? String(cartao.numero).replace(/\D/g, "").slice(-4)
                : undefined,
          });

      if (data?.pagamento?.formaPagamento === "pix" || data?.pagamento?.pixQrCode || data?.pagamento?.invoiceUrl) {
        setPagamentoPendente(data.pagamento);
        if (data?.pagamento?.formaPagamento === "pix" || data?.pagamento?.pixQrCode) {
          deveScrollPixRef.current = true;
        }
        toast.info(
          modoRenovacao
            ? formaPagamento === "pix" && formaPagamentoAtual === "cartao"
              ? "PIX de renovação gerado. A cobrança automática no cartão foi desativada."
              : "PIX de renovação gerado. Seu plano atual permanece ativo até a confirmação."
            : "Cobrança gerada. Conclua o pagamento para liberar o plano."
        );
      } else if (modoRenovacao && formaPagamento === "cartao") {
        toast.success("Renovação confirmada no cartão. Renovação automática ativada.");
      } else if (data?.assinatura?.status === "awaiting_payment" && formaPagamento === "cartao") {
        toast.info(
          asaasSandbox
            ? "Cobrança no cartão criada. No sandbox, se ficar pendente, confirme em Cobranças no Asaas."
            : "Aguardando confirmação do pagamento no cartão."
        );
      } else if (isTrial) {
        toast.success(`Trial ativado por ${PLANO_TRIAL.duracaoDias} dias! Assine um plano pago antes do fim do período.`);
      } else {
        toast.success("Pagamento confirmado. Plano ativo.");
      }
      notificarMudanca();
      await recarregar();
      if (modoRenovacao) setCheckoutRenovacaoAberto(false);
    } catch (error) {
      const amigavel = extrairErroAssinatura(error);
      const sugerirPix =
        formaPagamento === "cartao" &&
        /n[aã]o autoriz|n[aã]o foi confirmado|processar este cart[aã]o|tente novamente ou pague via PIX|ou use PIX|ou pague via PIX/i.test(
          amigavel
        );

      setOverlayPagamento(null);
      setProcessando(false);

      console.warn("[Assinatura] falha ao contratar/pagar:", {
        status: error?.response?.status,
        mensagem: error?.response?.data?.mensagem || error?.message,
        amigavel,
      });

      toast.dismiss("fz-assinatura-pagamento");
      if (sugerirPix) {
        toast.error(
          ({ closeToast }) => (
            <div className="assinatura-toast-pix-fallback">
              <span>{amigavel}</span>
              <button
                type="button"
                className="assinatura-toast-pix-fallback__btn"
                onClick={() => {
                  setFormaPagamento("pix");
                  closeToast?.();
                  toast.info("PIX selecionado. Confirme o pagamento abaixo para gerar o QR Code.", {
                    toastId: "fz-assinatura-pix-hint",
                    autoClose: 6000,
                  });
                }}
              >
                Pagar com PIX
              </button>
            </div>
          ),
          {
            autoClose: 14000,
            toastId: `fz-assinatura-pagamento-${Date.now()}`,
          }
        );
      } else {
        toast.error(amigavel, {
          autoClose: 12000,
          toastId: `fz-assinatura-pagamento-${Date.now()}`,
        });
        Swal.fire({
          icon: "error",
          title: "Não foi possível concluir",
          text: amigavel,
          confirmButtonText: "Entendi",
        });
      }
      return;
    } finally {
      setProcessando(false);
      setOverlayPagamento(null);
    }
  };

  const handleVerificarPagamentoPix = useCallback(async (silencioso = false) => {
    if (!silencioso) setProcessandoVerificarPix(true);
    try {
      const data = await sincronizarPagamentoPendenteApi();
      if (data?.status === "confirmado") {
        toast.success("Pagamento PIX confirmado!");
        setPagamentoPendente(null);
        setCheckoutRenovacaoAberto(false);
        notificarMudanca();
        await recarregar({ silencioso });
        return true;
      }
      if (!silencioso) {
        toast.info("Pagamento ainda pendente no Asaas. Aguarde alguns segundos e tente de novo.");
      }
      return false;
    } catch (error) {
      if (!silencioso) {
        toast.error(error.response?.data?.mensagem || "Erro ao verificar pagamento.");
      }
      return false;
    } finally {
      if (!silencioso) setProcessandoVerificarPix(false);
    }
  }, [recarregar]);

  useEffect(() => {
    if (!temPixPendente || !integracaoAsaas) return undefined;
    const timer = setInterval(async () => {
      const confirmado = await handleVerificarPagamentoPix(true);
      if (!confirmado) {
        await recarregar({ silencioso: true });
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [temPixPendente, integracaoAsaas, handleVerificarPagamentoPix, recarregar]);

  useEffect(() => {
    if (!integracaoAsaas || statusAtual !== "active") return undefined;
    const timer = setInterval(() => {
      void recarregar({ silencioso: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [statusAtual, integracaoAsaas, recarregar]);

  const handleSimularPagamento = async () => {
    setProcessando(true);
    try {
      await simularPagamentoApi();
      toast.success("Pagamento simulado com sucesso. Plano ativado.");
      setPagamentoPendente(null);
      notificarMudanca();
      await recarregar();
    } catch (error) {
      toast.error(error.response?.data?.mensagem || "Erro ao simular pagamento.");
    } finally {
      setProcessando(false);
    }
  };

  const handleFinalizar = () => {
    if (!planoSelecionado) {
      toast.warn("Selecione um plano acima.");
      return;
    }
    handleContratar(planoSelecionado, false);
  };

  const preencherCartaoTeste = () => {
    setCartao({ ...(asaasSandbox ? CARTAO_TESTE : CARTAO_TESTE_MOCK) });
    setFormaPagamento("cartao");
    toast.info("Cartão de teste preenchido. Aceite os termos e clique em Pagar com cartão.");
  };

  const handleCancelarPix = async () => {
    if (!podeGerenciar) return;

    const result = await Swal.fire({
      title: "Cancelar cobrança PIX?",
      html: "A cobrança atual será invalidada. Você poderá escolher outra forma de pagamento no checkout.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, cancelar PIX",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    setProcessandoCancelarPix(true);
    try {
      await cancelarPagamentoPendenteApi();
      setPagamentoPendente(null);
      setFormaPagamento("cartao");
      toast.success("Cobrança PIX cancelada. Escolha como deseja pagar.");
      notificarMudanca();
      await recarregar();
    } catch (error) {
      toast.error(error.response?.data?.mensagem || "Erro ao cancelar cobrança PIX.");
    } finally {
      setProcessandoCancelarPix(false);
    }
  };

  const handleAgendarTroca = async (planoId) => {
    if (!podeGerenciar || !planoAtivoPago) return;

    const planoInfo = PLANOS_PAGOS.find((p) => p.id === planoId);
    const preco = calcularPrecoPeriodo(planoInfo?.precoMensal || 0, periodo);
    const vigencia = assinatura?.periodoFim
      ? dayjs(assinatura.periodoFim).format("DD/MM/YYYY")
      : "—";

    const result = await Swal.fire({
      title: "Agendar troca de plano?",
      html: `O plano <strong>${planoInfo?.nome || planoId}</strong> (${labelPeriodo(periodo)}) entra em vigor em <strong>${vigencia}</strong>, no fim do ciclo atual.<br><br>Próxima cobrança: <strong>${formatarMoeda(preco)}</strong>.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Agendar troca",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#ff6b00",
    });

    if (!result.isConfirmed) return;

    setProcessandoTroca(true);
    try {
      await agendarTrocaPlanoApi({ plano: planoId, periodo });
      toast.success("Troca de plano agendada para o fim do ciclo.");
      notificarMudanca();
      await recarregar();
    } catch (error) {
      toast.error(error.response?.data?.mensagem || "Erro ao agendar troca de plano.");
    } finally {
      setProcessandoTroca(false);
    }
  };

  const handleDesativarRenovacaoAutomatica = async () => {
    if (!podeGerenciar || !podeDesativarRenovacaoAutomatica) return;

    const result = await Swal.fire({
      title: "Passar a pagar via PIX?",
      html: "A <strong>renovação automática no cartão será desativada</strong>. Na janela de renovação (5 dias antes do vencimento), você paga manualmente via PIX. Troca de plano agendada, se houver, será cancelada.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, usar PIX",
      cancelButtonText: "Manter cartão",
      confirmButtonColor: "#ff6b00",
    });

    if (!result.isConfirmed) return;

    setProcessandoTroca(true);
    try {
      await desativarRenovacaoAutomaticaApi();
      toast.success("Renovação automática desativada. Pague via PIX na próxima renovação.");
      notificarMudanca();
      await recarregar();
    } catch (error) {
      toast.error(error.response?.data?.mensagem || "Erro ao alterar forma de pagamento.");
    } finally {
      setProcessandoTroca(false);
    }
  };

  const handleCancelarTrocaAgendada = async () => {
    if (!podeGerenciar) return;

    const result = await Swal.fire({
      title: "Cancelar troca agendada?",
      html: "O plano atual permanece até o vencimento. Deseja cancelar a troca?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, cancelar troca",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    setProcessandoTroca(true);
    try {
      await cancelarTrocaAgendadaApi();
      toast.info("Troca de plano cancelada.");
      notificarMudanca();
      await recarregar();
    } catch (error) {
      toast.error(error.response?.data?.mensagem || "Erro ao cancelar troca agendada.");
    } finally {
      setProcessandoTroca(false);
    }
  };

  const handleCancelar = async () => {
    if (!podeGerenciar) return;

    const result = await Swal.fire({
      title: "Cancelar assinatura?",
      html: "O acesso ao painel e ao app será <strong>suspenso na hora</strong>. Deseja continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, cancelar assinatura",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    setProcessando(true);
    try {
      await cancelarAssinaturaApi();
      toast.info("Assinatura cancelada. Estabelecimento suspenso.");
      notificarMudanca();
      await recarregar();
    } catch (error) {
      toast.error(error.response?.data?.mensagem || "Erro ao cancelar assinatura.");
    } finally {
      setProcessando(false);
    }
  };

  if (loading) return <p className="muted">Carregando plano e assinatura...</p>;

  return (
    <div className="assinatura-page">
      <LoadingOverlay aberto={Boolean(overlayPagamento)} mensagem={overlayPagamento || "Processando…"} />

      <section className="assinatura-hero">
        <div>
          <span className="eyebrow">Monetização</span>
          <h1>Plano e assinatura</h1>
          <p>Escolha o plano ideal para o seu estabelecimento. Pagamento antecipado por período com renovação automática.</p>
        </div>
      </section>

      {isSuperAdmin && isento && (
        <div className="assinatura-alert assinatura-alert--info">
          <Shield size={18} />
          <span>Você é <strong>Super Admin</strong> e está isento de assinatura. Esta tela é apenas para visualização e testes.</span>
        </div>
      )}

      {!isAdmin && (
        <div className="assinatura-alert assinatura-alert--warning">
          <AlertCircle size={18} />
          <span>Somente o administrador do estabelecimento pode contratar ou alterar planos.</span>
        </div>
      )}

      {acesso?.somenteLeitura && (
        <div className="assinatura-alert assinatura-alert--warning">
          <AlertCircle size={18} />
          <span>Modo <strong>leitura</strong>: explore o painel por até 2 dias. Escolha um plano para liberar alterações.</span>
        </div>
      )}

      {emCarencia && (
        <div className="assinatura-alert assinatura-alert--warning">
          <AlertCircle size={18} />
          <span>
            Período expirado. Você tem até{" "}
            <strong>{dayjs(assinatura.carenciaAte || acesso?.carenciaAte).format("DD/MM/YYYY HH:mm")}</strong>{" "}
            para renovar antes da suspensão total.
          </span>
        </div>
      )}

      {temPixPendente && pagamentoPixAtivo && (
        <div
          ref={pixPanelRef}
          className="card-block assinatura-checkout assinatura-checkout--pendente"
        >
          <div className="assinatura-checkout__head">
            <h2>Pagamento pendente</h2>
            <p className="muted">
              Finalize o PIX para ativar o plano. O acesso completo é liberado assim que o pagamento for confirmado.
            </p>
          </div>

          <PixPagamentoPanel
            pagamento={pagamentoPixAtivo}
            valor={faturaPendente?.valor}
            geradoEm={faturaPendente?.data || pagamentoPendente?.geradoEm}
            onSimular={handleSimularPagamento}
            onVerificarPagamento={() => handleVerificarPagamentoPix(false)}
            onRecarregarQr={recarregar}
            onCancelarPix={handleCancelarPix}
            modoTeste={modoTesteAssinatura}
            asaasSandbox={asaasSandbox}
            integracaoAsaas={integracaoAsaas}
            podeCancelar={podeGerenciar}
            processando={processando}
            processandoVerificar={processandoVerificarPix}
            processandoCancelar={processandoCancelarPix}
          />

        </div>
      )}

      <div className={`assinatura-status-card assinatura-status-card--${bannerTone}`}>
        <div className="assinatura-status-card__header">
          <div className="assinatura-status-card__intro">
            <span className="eyebrow">Situação atual</span>
            <strong className="assinatura-status-card__titulo">
              {isSuperAdmin ? "Isento (Super Admin)" : (emCarencia ? STATUS_LABELS.carencia : STATUS_LABELS[statusAtual]) || statusAtual}
            </strong>
          </div>
          {mostrarCancelar && (
            <button type="button" className="btn btn-outline btn-danger-outline btn-sm" onClick={handleCancelar}>
              Cancelar assinatura (teste)
            </button>
          )}
        </div>

        {!isSuperAdmin && statusAtual !== "pending_plan" && (planoAtual || statusAtual === "trial") && (
          <div className="assinatura-status-card__chips">
            {planoAtual && (
              <span className="assinatura-status-chip">
                {labelPlano(planoAtual)} · {labelPeriodo(assinatura.periodo)}
              </span>
            )}
            {statusAtual === "trial" && (
              <span className="assinatura-status-chip assinatura-status-chip--accent">
                {diasRestantes} dia(s) restante(s)
              </span>
            )}
            {statusAtual === "trial" && (assinatura?.trialFim || assinatura?.periodoFim) && (
              <span className="assinatura-status-chip">
                até {dayjs(assinatura.trialFim || assinatura.periodoFim).format("DD/MM/YYYY")}
              </span>
            )}
            {statusAtual === "active" && diasRestantes === 0 && assinatura?.periodoFim && renovacaoManual && (
              <span className="assinatura-status-chip assinatura-status-chip--countdown">
                <VencimentoCountdown alvo={assinatura.periodoFim} variant="chip" />
              </span>
            )}
            {statusAtual === "active" && diasRestantes > 0 && (
              <span className="assinatura-status-chip assinatura-status-chip--accent">
                {assinatura.diasRestantes} dia(s) restante(s)
              </span>
            )}
            {statusAtual === "active" && assinatura?.periodoFim && (
              <span className="assinatura-status-chip">
                até {dayjs(assinatura.periodoFim).format("DD/MM/YYYY")}
              </span>
            )}
            {planoAtual && statusAtual === "active" && (
              <span className="assinatura-status-chip">
                {assinatura.renovacaoAutomatica ? "Renovação automática" : "Renovação manual"}
              </span>
            )}
          </div>
        )}

        {planoAtivoPago && formaPagamentoAtual && (
          <div className="assinatura-pagamento-info">
            <p className="assinatura-pagamento-info__titulo">Forma de pagamento</p>
            <div className="assinatura-pagamento-info__grid">
              <div
                className={`assinatura-pagamento-info__metodo assinatura-pagamento-info__metodo--${
                  renovacaoViaPix ? "pix" : formaPagamentoAtual
                }`}
              >
                <FormaPagamentoIcon forma={renovacaoViaPix ? "pix" : formaPagamentoAtual} size={34} />
                <div className="assinatura-pagamento-info__metodo-copy">
                  <strong>{renovacaoViaPix ? "PIX" : "Cartão de crédito"}</strong>
                  <span>
                    {renovacaoViaPix
                      ? formaPagamentoAtual === "cartao"
                        ? "Próxima renovação via PIX — cobrança automática no cartão desativada"
                        : "Pagamento manual a cada ciclo"
                      : assinatura.cartaoUltimos4
                        ? `•••• ${assinatura.cartaoUltimos4} · renovação automática`
                        : "Renovação automática a cada ciclo"}
                  </span>
                </div>
              </div>
              <div className="assinatura-pagamento-info__detalhe">
                {renovacaoViaCartaoAuto && proximaCobranca?.em ? (
                  <>
                    <span className="assinatura-pagamento-info__label">Próxima cobrança</span>
                    <span>
                      {dayjs(proximaCobranca.em).format("DD/MM/YYYY")}
                      {proximaCobranca.valor != null && (
                        <> · {formatarMoeda(proximaCobranca.valor)}</>
                      )}
                    </span>
                    {proximaCobranca.trocaAgendada && (
                      <small className="muted">Inclui troca de plano agendada</small>
                    )}
                  </>
                ) : assinatura.periodoFim ? (
                  <>
                    <span className="assinatura-pagamento-info__label">Vencimento</span>
                    <span>{dayjs(assinatura.periodoFim).format("DD/MM/YYYY")}</span>
                    <small className="muted">
                      {modoRenovacao || emCarencia
                        ? "Escolha o plano e clique em Renovar agora."
                        : renovacaoManual
                          ? "Renovação disponível a partir de 5 dias antes do vencimento."
                          : "Renovação automática no cartão na data acima."}
                    </small>
                  </>
                ) : null}
              </div>
            </div>
            {podeDesativarRenovacaoAutomatica && podeGerenciar && !temPixPendente && (
              <div className="assinatura-pagamento-info__actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={handleDesativarRenovacaoAutomatica}
                  disabled={processandoTroca}
                >
                  Passar a pagar via PIX
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {upgradeAgendado && (
        <div className="assinatura-alert assinatura-alert--info assinatura-alert--troca">
          <Check size={18} />
          <span>
            Troca agendada: <strong>{labelPlano(upgradeAgendado.plano)}</strong> ({labelPeriodo(upgradeAgendado.periodo)}) a partir de{" "}
            <strong>{dayjs(upgradeAgendado.vigenciaEm).format("DD/MM/YYYY")}</strong>.
            {proximaCobranca?.valor != null && <> Próxima cobrança: {formatarMoeda(proximaCobranca.valor)}.</>}
          </span>
          {podeGerenciar && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={handleCancelarTrocaAgendada}
              disabled={processandoTroca}
            >
              Cancelar troca
            </button>
          )}
        </div>
      )}

      {modoRenovacao && !temPixPendente && !checkoutRenovacaoAberto && (
        <div
          className={`assinatura-alert assinatura-alert--warning assinatura-alert--renovacao${
            ultimoDiaRenovacao ? " assinatura-alert--ultimo-dia" : ""
          }`}
        >
          <AlertCircle size={18} />
          {ultimoDiaRenovacao ? (
            <div className="assinatura-alert__countdown-wrap">
              <VencimentoCountdown alvo={assinatura.periodoFim} variant="alert" label="Renove em" />
              <span>Hoje é o último dia para renovar. Escolha o plano e clique em Renovar agora.</span>
            </div>
          ) : (
            <span>
              {textoCountdownRenovacao(diasRestantes)} Escolha o plano abaixo e clique em Renovar agora.
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => abrirCheckoutRenovacao()}
          >
            Renovar agora
          </button>
        </div>
      )}

      {mostrarCheckoutRenovacao && (
        <div className="assinatura-alert assinatura-alert--info assinatura-alert--renovacao">
          <Check size={18} />
          <span>
            Plano selecionado: <strong>{planoEscolhido.nome}</strong> · {labelPeriodo(periodo)} ·{" "}
            {formatarMoeda(precoCheckout)}. Escolha PIX ou cartão abaixo.
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setCheckoutRenovacaoAberto(false)}
          >
            Fechar checkout
          </button>
        </div>
      )}

      {planoAtivoPago && podeAgendarTroca && !upgradeAgendado && (
        <p className="assinatura-scheduling-hint muted">
          Para trocar de plano no cartão, escolha o período abaixo e clique em <strong>Agendar</strong> no card desejado.
        </p>
      )}

      {planoAtivoPago && renovacaoManual && !modoRenovacao && !temPixPendente && (
        <p className="assinatura-scheduling-hint muted">
          Com PIX, a troca de plano é feita na renovação — disponível a partir de 5 dias antes do vencimento.
        </p>
      )}

      {planoAtivoPago && assinatura?.renovacaoAutomatica && !temPixPendente && (
        <p className="assinatura-scheduling-hint muted">
          Renovação automática no cartão. Para pagar via PIX no próximo ciclo, use{" "}
          <strong>Passar a pagar via PIX</strong> em Forma de pagamento.
        </p>
      )}

      {!isSuperAdmin && (
      <>
      <div
        className={`assinatura-period-toggle${planoAtivoPago && podeAgendarTroca && !upgradeAgendado ? " assinatura-period-toggle--scheduling" : ""}${planoAtivoPago && upgradeAgendado ? " assinatura-period-toggle--locked" : ""}${modoRenovacao ? " assinatura-period-toggle--scheduling" : ""}`}
        role="tablist"
        aria-label="Período de cobrança"
      >
        {PERIODOS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={periodo === p.id}
            className={periodo === p.id ? "active" : ""}
            onClick={() => {
              if (!planoAtivoPago || !upgradeAgendado || modoRenovacao) setPeriodo(p.id);
            }}
            disabled={planoAtivoPago && Boolean(upgradeAgendado) && !modoRenovacao}
            title={
              modoRenovacao
                ? "Período da renovação"
                : planoAtivoPago && upgradeAgendado
                  ? "Período da troca agendada"
                  : planoAtivoPago && podeAgendarTroca
                    ? "Período da próxima cobrança ao agendar troca"
                    : undefined
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="assinatura-grid">
        {podeContratar && (
        <article className={`assinatura-card assinatura-card--trial ${planoSelecionado === "trial" ? "is-selected" : ""}`}>
          <div className="assinatura-card__head">
            <Sparkles size={22} />
            <h2>{PLANO_TRIAL.nome}</h2>
            <p className="assinatura-card__price">Grátis <small>/ {PLANO_TRIAL.duracaoDias} dias</small></p>
          </div>
          <ul className="assinatura-card__features">
            {PLANO_TRIAL.recursos.map((item) => (
              <li key={item}><Check size={14} /> {item}</li>
            ))}
          </ul>
          {assinatura?.trialUsado && (
            <p className="assinatura-card__note">Trial já utilizado neste estabelecimento.</p>
          )}
          <label className="assinatura-trial-select">
            Plano após o trial
            <select
              value={planoPosTrial}
              onChange={(e) => setPlanoPosTrial(e.target.value)}
              disabled={!podeGerenciar || assinatura?.trialUsado || processando}
            >
              {PLANOS_PAGOS.map((p) => (
                <option key={p.id} value={p.id}>{p.nome} · {labelPeriodo(periodo)}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-outline"
            disabled={!podeGerenciar || assinatura?.trialUsado || processando}
            onClick={() => handleContratar(null, true)}
          >
            Iniciar trial
          </button>
        </article>
        )}

        {PLANOS_PAGOS.map((plano) => {
          const Icon = iconePlano[plano.id] || User;
          const precoTotal = calcularPrecoPeriodo(plano.precoMensal, periodo);
          const equivMensal = calcularEquivalenteMensal(plano.precoMensal, periodo);
          const periodoInfo = PERIODOS.find((p) => p.id === periodo);
          const showEquiv = periodo !== "monthly";
          const isPlanoAtual =
            planoAtivoPago && plano.id === planoAtual && periodo === assinatura?.periodo;
          const trocaAgendadaEste =
            upgradeAgendado?.plano === plano.id && upgradeAgendado?.periodo === periodo;
          const podeAgendarEste =
            planoAtivoPago &&
            podeGerenciar &&
            podeAgendarTroca &&
            !isPlanoAtual &&
            !trocaAgendadaEste;
          const podeSelecionarRenovacao = modoRenovacao && podeGerenciar && !temPixPendente;

          return (
            <article
              key={plano.id}
              className={`assinatura-card ${plano.destaque ? "assinatura-card--destaque" : ""} ${planoSelecionado === plano.id && podeContratar ? "is-selected" : ""} ${isPlanoAtual ? "assinatura-card--atual" : ""}`}
            >
              {isPlanoAtual && <span className="assinatura-card__badge assinatura-card__badge--atual">Plano atual</span>}
              {trocaAgendadaEste && (
                <span className="assinatura-card__badge assinatura-card__badge--agendada">Troca agendada</span>
              )}
              {!isPlanoAtual && plano.destaque && !planoAtivoPago && (
                <span className="assinatura-card__badge">Mais popular</span>
              )}
              <div className="assinatura-card__head">
                <Icon size={22} />
                <h2>{plano.nome}</h2>
                <p className="assinatura-card__price">
                  {formatarMoeda(precoTotal)}
                  <small> / {periodoInfo?.label.toLowerCase()}</small>
                </p>
                {showEquiv && (
                  <p className="assinatura-card__equiv">≈ {formatarMoeda(equivMensal)}/mês</p>
                )}
                <p className="assinatura-card__prof">{plano.profissionais}</p>
              </div>
              <ul className="assinatura-card__features">
                {plano.recursos.map((item) => (
                  <li key={item}><Check size={14} /> {item}</li>
                ))}
              </ul>
              {podeContratar && !modoRenovacao ? (
                <button
                  type="button"
                  className={planoSelecionado === plano.id ? "btn" : "btn btn-outline"}
                  disabled={!podeGerenciar || processando}
                  onClick={() => setPlanoSelecionado(plano.id)}
                >
                  {planoSelecionado === plano.id ? `${plano.nome} selecionado` : `Selecionar ${plano.nome}`}
                </button>
              ) : podeSelecionarRenovacao ? (
                <button
                  type="button"
                  className={planoSelecionado === plano.id ? "btn" : "btn btn-outline"}
                  disabled={processando}
                  onClick={() => abrirCheckoutRenovacao(plano.id)}
                >
                  {planoSelecionado === plano.id ? `${plano.nome} para renovação` : `Renovar com ${plano.nome}`}
                </button>
              ) : isPlanoAtual ? (
                <p className="assinatura-card__foot assinatura-card__foot--atual">
                  Seu plano até {dayjs(assinatura?.periodoFim).format("DD/MM/YYYY")}
                </p>
              ) : trocaAgendadaEste ? (
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={processandoTroca}
                  onClick={handleCancelarTrocaAgendada}
                >
                  Cancelar troca agendada
                </button>
              ) : podeAgendarEste ? (
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={processandoTroca || Boolean(upgradeAgendado)}
                  title={upgradeAgendado ? "Cancele a troca atual para agendar outra" : undefined}
                  onClick={() => handleAgendarTroca(plano.id)}
                >
                  Agendar {plano.nome}
                </button>
              ) : renovacaoManual && planoAtivoPago && !modoRenovacao ? (
                <p className="assinatura-card__foot muted">Renovação a partir de 5 dias antes do vencimento</p>
              ) : (
                <p className="assinatura-card__foot muted">Mesmo plano e período do ciclo atual</p>
              )}
            </article>
          );
        })}
      </div>

      {((!planoAtivoPago && podeContratar) || mostrarCheckoutRenovacao) && !temPixPendente && (
      <section className="card-block assinatura-checkout">
        <h2>{modoRenovacao ? "Renovar assinatura" : "Checkout"}</h2>
        <p className="muted">
          {modoRenovacao
            ? "Escolha plano, período e forma de pagamento. Com PIX, o plano atual permanece ativo até a confirmação; com cartão, a renovação automática é ativada na hora."
            : "1. Selecione o plano · 2. Escolha a forma de pagamento · 3. Confirme abaixo"}
        </p>

        <div className="assinatura-checkout-resumo">
          <div className="assinatura-checkout-resumo__info">
            <span className="eyebrow">Plano selecionado</span>
            <strong>{planoEscolhido.nome} · {labelPeriodo(periodo)}</strong>
          </div>
          <p className="assinatura-checkout-resumo__valor">{formatarMoeda(precoCheckout)}</p>
        </div>

        <h3 className="assinatura-checkout__subtitle">Forma de pagamento</h3>
        <p className="muted assinatura-pagamento-hint">
          PIX costuma confirmar na hora. Cartão depende da autorização do banco e dos dados do titular.
        </p>
        <div className="assinatura-pagamento-grid">
          {FORMAS_PAGAMENTO.map((forma) => (
              <button
                key={forma.id}
                type="button"
                className={`assinatura-pagamento-btn ${formaPagamento === forma.id ? "active" : ""}`}
                onClick={() => setFormaPagamento(forma.id)}
                disabled={!podeGerenciar}
              >
                <FormaPagamentoIcon forma={forma.id} size={30} />
                <span>{forma.label}</span>
                {forma.descricao && <small>{forma.descricao}</small>}
              </button>
            ))}
        </div>

        {formaPagamento === "cartao" && (
          <div className="assinatura-cartao-mock">
            <h3 className="assinatura-checkout__subtitle">Titular do cartão (pagador)</h3>
            <p className="muted assinatura-titular-checkout__hint">
              Dados do dono do cartão exigidos pelo Asaas. O nome enviado é o mesmo preenchido em &quot;Nome no cartão&quot;.
            </p>
            <div className="form-grid-2">
              <label>
                CPF/CNPJ
                <IMaskInput
                  className="field"
                  mask={mascaraCpfCnpj}
                  value={titular.cpfCnpj}
                  onAccept={(value) => setTitular((t) => ({ ...t, cpfCnpj: value }))}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  disabled={!podeGerenciar || processando}
                />
              </label>
              <label>
                Telefone
                <IMaskInput
                  className="field"
                  mask="(00) 00000-0000"
                  value={titular.telefone}
                  onAccept={(value) => setTitular((t) => ({ ...t, telefone: value }))}
                  placeholder="(11) 99999-9999"
                  inputMode="numeric"
                  disabled={!podeGerenciar || processando}
                />
              </label>
              <label>
                E-mail
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="email@exemplo.com"
                  value={titular.email}
                  onChange={(e) => setTitular((t) => ({ ...t, email: e.target.value }))}
                  disabled={!podeGerenciar || processando}
                />
              </label>
              <label>
                CEP
                <IMaskInput
                  className="field"
                  mask="00000-000"
                  value={titular.cep}
                  onAccept={(value) => setTitular((t) => ({ ...t, cep: value }))}
                  placeholder="00000-000"
                  inputMode="numeric"
                  disabled={!podeGerenciar || processando}
                />
              </label>
              <label>
                Número do endereço
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="123"
                  value={titular.enderecoNumero}
                  onChange={(e) =>
                    setTitular((t) => ({
                      ...t,
                      enderecoNumero: e.target.value.replace(/[^\dA-Za-z]/g, "").slice(0, 10),
                    }))
                  }
                  disabled={!podeGerenciar || processando}
                />
              </label>
            </div>

            {(modoTesteAssinatura) && (
              <div className="assinatura-cartao-teste">
                <p className="muted">
                  {asaasSandbox
                    ? "Sandbox Asaas — cartão Mastercard 5162…8829 (CVV 318). A cobrança deve aprovar na hora; se ficar pendente, confirme em Cobranças no Asaas."
                    : "Ambiente de teste — use um cartão fictício aprovado na hora."}
                </p>
                <button type="button" className="btn btn-outline btn-sm" onClick={preencherCartaoTeste} disabled={!podeGerenciar}>
                  {asaasSandbox ? "Usar cartão sandbox (5162…)" : "Usar cartão de teste (4111…)"}
                </button>
              </div>
            )}
            <h3 className="assinatura-checkout__subtitle">Dados do cartão</h3>
            <div className="form-grid-2">
              <label className="assinatura-cartao-numero-label">
                Número do cartão
                <div className="assinatura-cartao-numero-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                    value={cartao.numero}
                    onChange={(e) => setCartao((c) => ({ ...c, numero: formatarNumeroCartao(e.target.value) }))}
                    disabled={!podeGerenciar || processando}
                  />
                  <BandeiraCartao bandeira={bandeiraCartao} />
                </div>
                {cartao.numero.replace(/\D/g, "").length >= 4 && !bandeiraCartao && (
                  <small className="assinatura-cartao-bandeira-hint">Bandeira não identificada — Visa, Mastercard, Elo, etc.</small>
                )}
              </label>
              <label>
                Nome no cartão
                <input
                  type="text"
                  autoComplete="cc-name"
                  placeholder="Como impresso no cartão"
                  value={cartao.nome}
                  onChange={(e) => setCartao((c) => ({ ...c, nome: e.target.value.toUpperCase() }))}
                  disabled={!podeGerenciar || processando}
                />
              </label>
              <label>
                Validade
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  placeholder="MM/AA"
                  value={cartao.validade}
                  onChange={(e) => setCartao((c) => ({ ...c, validade: formatarValidadeCartao(e.target.value) }))}
                  disabled={!podeGerenciar || processando}
                />
              </label>
              <label>
                CVV
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder="000"
                  value={cartao.cvv}
                  onChange={(e) => setCartao((c) => ({ ...c, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  disabled={!podeGerenciar || processando}
                />
              </label>
            </div>
          </div>
        )}

        {formaPagamento === "pix" && (
          <>
            {!documentoFaturamentoOk && (
              <div className="assinatura-alert assinatura-alert--warning" role="alert">
                <strong>CPF/CNPJ de cobrança obrigatório.</strong>
                <span>
                  {" "}
                  Cadastre em{" "}
                  <a href="/estabelecimentos">Estabelecimentos</a> antes de gerar o PIX.
                </span>
              </div>
            )}
            <p className="muted assinatura-pix-info">
              {modoRenovacao
                ? formaPagamentoAtual === "cartao" && assinatura?.renovacaoAutomatica
                  ? "Ao gerar o PIX, a cobrança automática no cartão será desativada. Seu plano permanece ativo até confirmarmos o pagamento."
                  : "Seu plano atual permanece ativo até confirmarmos o PIX. O novo ciclo estende a partir do vencimento."
                : "Ao confirmar, geramos o QR Code e o copia e cola. O plano só ativa após o pagamento."}
            </p>
          </>
        )}

        {modoRenovacao && formaPagamento === "cartao" && (
          <p className="muted assinatura-pix-info">
            Ao confirmar, cobramos no cartão e ativamos a renovação automática. O novo ciclo estende a partir do vencimento atual.
          </p>
        )}

        <div ref={termosRef}>
          <TermosAceite
            aceite={aceiteTermos}
            onChange={setAceiteTermos}
            disabled={!podeGerenciar}
            onVerTermos={mostrarTermos}
            destacado={termosDestacado}
          />
        </div>

        <div className="assinatura-checkout-actions">
          <button
            type="button"
            className="btn btn-lg"
            disabled={
              !podeGerenciar ||
              processando ||
              ((formaPagamento === "pix" || formaPagamento === "boleto") && !documentoFaturamentoOk)
            }
            onClick={handleFinalizar}
          >
            {processando
              ? "Processando…"
              : modoRenovacao && formaPagamento === "cartao"
                ? `Renovar com cartão · ${formatarMoeda(precoCheckout)}`
                : modoRenovacao || formaPagamento === "pix"
                  ? `Gerar PIX${modoRenovacao ? " de renovação" : ""} · ${formatarMoeda(precoCheckout)}`
                  : `Pagar ${formatarMoeda(precoCheckout)} com cartão`}
          </button>
        </div>
      </section>
      )}

      {podeContratar && temPixPendente && (
        <div className="assinatura-alert assinatura-alert--info">
          <AlertCircle size={18} />
          <span>
            Há um <strong>PIX pendente</strong> aguardando pagamento acima.
            Para escolher cartão ou gerar outro PIX, cancele a cobrança atual primeiro.
          </span>
        </div>
      )}
      </>
      )}

      <section className="assinatura-faturas">
        <details className="fz-accordion fz-accordion--faturas card-block">
          <summary className="fz-accordion__summary">
            <span className="fz-accordion__summary-main">
              <FileText size={18} className="fz-accordion__summary-icon" aria-hidden="true" />
              <span className="fz-accordion__summary-text">
                <span className="eyebrow">Financeiro</span>
                Histórico de faturas
                {(assinatura?.faturas || []).length > 0 && (
                  <span className="fz-accordion__count">
                    {" "}
                    ({(assinatura?.faturas || []).length})
                  </span>
                )}
              </span>
            </span>
            <span className="fz-accordion__summary-hint">Expandir</span>
          </summary>
          <div className="fz-accordion__content assinatura-faturas__content">
        {(assinatura?.faturas || []).length === 0 ? (
          <p className="muted">Nenhuma fatura registrada.</p>
        ) : isMobile ? (
          <div className="assinatura-faturas-list">
            {(assinatura?.faturas || []).map((fat) => (
              <article key={fat.id} className="assinatura-fatura-card">
                <div className="assinatura-fatura-card__head">
                  <strong>{fat.descricao}</strong>
                  <span className={`badge ${classeStatusFatura(fat.status)}`}>
                    {labelStatusFatura(fat.status)}
                  </span>
                </div>
                <dl className="assinatura-fatura-card__meta">
                  <div>
                    <dt>Data</dt>
                    <dd>{dayjs(fat.data).format("DD/MM/YYYY")}</dd>
                  </div>
                  <div>
                    <dt>Forma</dt>
                    <dd>
                      {FORMAS_PAGAMENTO.find((f) => f.id === fat.forma)?.label || fat.forma}
                    </dd>
                  </div>
                  <div>
                    <dt>Valor</dt>
                    <dd>{formatarMoeda(fat.valor)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <div className="assinatura-faturas-table-wrap">
            <table className="assinatura-faturas-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th>Forma</th>
                  <th>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(assinatura?.faturas || []).map((fat) => (
                  <tr key={fat.id}>
                    <td>{dayjs(fat.data).format("DD/MM/YYYY")}</td>
                    <td>{fat.descricao}</td>
                    <td>{FORMAS_PAGAMENTO.find((f) => f.id === fat.forma)?.label || fat.forma}</td>
                    <td>{formatarMoeda(fat.valor)}</td>
                    <td>
                      <span className={`badge ${classeStatusFatura(fat.status)}`}>
                        {labelStatusFatura(fat.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </div>
        </details>
      </section>
    </div>
  );
};

export default Assinatura;
